import React, { useState } from 'react';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
} from './ui/Dialog';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';

interface TriggerModalProps {
  isOpen: boolean;
  onClose: () => void;
  playbookId: string;
  playbookName: string;
  onSubmit: (playbookId: string, inputData: string) => Promise<void>;
}

export const TriggerModal: React.FC<TriggerModalProps> = ({
  isOpen,
  onClose,
  playbookId,
  playbookName,
  onSubmit
}) => {
  const [inputData, setInputData] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();
  const maxLength = 500;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputData.trim()) {
      setError('Input data is required');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await onSubmit(playbookId, inputData);
      addToast({
        title: 'Success',
        description: 'Playbook triggered successfully. Please wait 5-10 minutes and then check the execution logs.',
        variant: 'default',
      });
      setInputData('');
      onClose();
    } catch (err) {
      console.error('Error triggering playbook:', err);
      setError(err instanceof Error ? err.message : 'Failed to trigger playbook');
      addToast({
        title: 'Error',
        description: 'Failed to trigger playbook',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setInputData(value);
      setError(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Trigger Playbook: {playbookName}</DialogTitle>
            <DialogDescription className="text-sm text-gray-500 mt-2">
              Enter input data for this playbook execution.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6 space-y-4">
            <div>
              <label htmlFor="input-data" className="block text-sm font-medium text-gray-700 mb-1">
                Input Data <span className="text-red-500">*</span>
              </label>
              <textarea
                id="input-data"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
                rows={5}
                placeholder="Enter input data for the playbook (max 500 characters)"
                value={inputData}
                onChange={handleInputChange}
                disabled={isSubmitting}
                required
              />
              <div className="flex justify-between mt-1">
                {error && <p className="text-sm text-red-600">{error}</p>}
                <p className="text-sm text-gray-500 ml-auto">
                  {inputData.length}/{maxLength}
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter className="bg-gray-50 px-6 py-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !inputData.trim()}
              className="bg-black hover:bg-gray-800 text-white"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TriggerModal;
